import { BatteryParameters } from '../../generated/graphql'
import { reduceToSum, sum } from '../../util/array-utility'
import {
  EnergyType,
  isFromEnvironment,
  isProvided,
  PrimaryEnergyFlow,
  SecondaryEnergyFlow,
  WasteType,
} from '../EnergyFlow'
import {
  createHourlyFromEnvironment,
  createHourlyProvided,
  createSecondaryHourlyRequirement,
  createSecondaryHourlyWaste,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
  SecondaryHourlyEnergyFlow,
} from '../HourlyEnergyFlow'
import { HourlyVariables, Provider } from './common/Provider'

type BatteryHourlyVariables = HourlyVariables & {
  remainingDemand?: PrimaryHourlyEnergyFlow
  overproductions?: SecondaryHourlyEnergyFlow[]
}
type BatteryHourlyVariablesSingle = BatteryHourlyVariables & {
  remainingDemand?: PrimaryHourlyEnergyFlow
  overproduction?: SecondaryHourlyEnergyFlow
}
/**
 * The main purpose of this provider is to act as a temporary energy storage to balance daily use by providing a means to temporarily store energy into the environment.
 *
 * Secondarily, this provider acts as a charge controller that can handle unlimited demand and overproduction matching.
 */
export class BatteryProvider extends Provider<
  BatteryParameters,
  BatteryHourlyVariables
> {
  public override readonly providedEnergyTypes = [EnergyType.Electricity]

  public remainingCapacity = 0

  public remainingCapacityHourSeries: number[] = []

  public overproductionHourSeries: number[] = []

  /** Hourly remaining charge power */
  private remainingChargePower = 0

  /** Hourly remaining discharge power */
  private remainingDischargePower = 0

  protected override getHourlyVariables(
    hour: number,
    demands: PrimaryHourlyEnergyFlow[],
    overproductions: SecondaryHourlyEnergyFlow[],
  ): BatteryHourlyVariables {
    if (demands.length > 1) {
      throw new Error('Battery provider can only provide for one energy type')
    }

    this.remainingCapacityHourSeries[hour] = this.remainingCapacity

    const overProductionValues = overproductions.map((o) => o.value)
    this.overproductionHourSeries[hour] = sum(overProductionValues)

    return {
      ...super.getHourlyVariables(hour, demands, overproductions),
      remainingDemand: demands.length === 1 ? demands[0] : undefined,
      overproductions,
    }
  }

  /**
   * Trigger early return when both demands AND overproduction is missing.
   */
  protected override shouldTriggerEarlyReturn(
    combinedInputEnergyFlows: PrimaryEnergyFlow[],
    overproductions?: SecondaryEnergyFlow[],
  ): boolean {
    return (
      combinedInputEnergyFlows.length === 0 &&
      (!overproductions || overproductions?.length === 0)
    )
  }

  protected override provideForHour({
    remainingDemand,
    overproductions,
  }: BatteryHourlyVariables): HourlyEnergyFlow[] {
    this.remainingChargePower = this.parameters.chargePower
    this.remainingDischargePower = this.parameters.dischargePower
    if (!overproductions || overproductions?.length === 0) {
      return this.provideForHourSingle({ remainingDemand })
    }

    const remaining = remainingDemand ? { ...remainingDemand } : undefined
    const allFlows = overproductions.flatMap((overproduction) => {
      const flows = this.provideForHourSingle({
        remainingDemand: remaining,
        overproduction,
      })
      if (remaining) {
        remaining.value -= flows.find(isProvided)?.value ?? 0
      }
      return flows
    })

    const result = allFlows.filter(
      (flow) => !isProvided(flow) && !isFromEnvironment(flow),
    )

    const providedValue = allFlows
      .filter(isProvided)
      .map((flow) => flow.value)
      .reduce(reduceToSum, 0)
    const provided = allFlows.find(isProvided)
    if (provided) {
      provided.value = providedValue
      result.push(provided)
    }

    const fromEnvironmentValue = allFlows
      .filter(isFromEnvironment)
      .map((flow) => flow.value)
      .reduce(reduceToSum, 0)
    const fromEnv = allFlows.find(isFromEnvironment)
    if (fromEnv) {
      fromEnv.value = fromEnvironmentValue
      result.push(fromEnv)
    }

    return result
  }

  private provideForHourSingle({
    remainingDemand,
    overproduction,
  }: BatteryHourlyVariablesSingle): HourlyEnergyFlow[] {
    const flows = []

    const remainingDemandValue = remainingDemand?.value ?? 0
    const overproductionValue = overproduction?.value ?? 0

    const overlappingPower = Math.min(remainingDemandValue, overproductionValue)

    const chargePower = this.getChargePower(
      overproductionValue,
      overlappingPower,
    )

    const dischargePower = this.getDischargePower(
      remainingDemandValue,
      overlappingPower,
    )

    if (remainingDemand) {
      const provided = createHourlyProvided(
        remainingDemand.demandId,
        remainingDemand.energyType,
        dischargePower + overlappingPower,
      )
      const fromBattery = createHourlyFromEnvironment(
        provided.demandId,
        provided.energyType,
        dischargePower,
      )
      flows.push(provided, fromBattery)
    }

    if (overproduction) {
      const requirement = createSecondaryHourlyRequirement(
        overproduction.providerId,
        overproduction.energyType,
        chargePower + overlappingPower,
      )
      const toBattery = createSecondaryHourlyWaste(
        requirement.providerId,
        requirement.energyType,
        chargePower,
        WasteType.Recycled,
      )
      flows.push(requirement, toBattery)
    }

    this.remainingCapacity += chargePower - dischargePower
    this.remainingChargePower -= chargePower
    this.remainingDischargePower -= dischargePower

    return flows
  }

  private getChargePower(
    overproductionValue: number,
    overlappingPower: number,
  ) {
    return Math.min(
      overproductionValue - overlappingPower,
      this.remainingChargePower,
      this.parameters.capacity - this.remainingCapacity,
    )
  }

  private getDischargePower(
    remainingDemandValue: number,
    overlappingPower: number,
  ) {
    return Math.min(
      remainingDemandValue - overlappingPower,
      this.remainingDischargePower,
      this.remainingCapacity,
    )
  }
}

export function isBatteryProvider(
  provider: Provider,
): provider is BatteryProvider {
  return provider instanceof BatteryProvider
}
